// ────────────────────────────────────────────────
// src/components/Features.jsx
// ────────────────────────────────────────────────
import React from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { FiSun, FiTool, FiDollarSign } from "react-icons/fi";

/* ---------- Datos de servicios ---------- */
const services = [
  {
    icon: FiSun,
    title: "Instalación Solar Residencial",
    desc: "Sistemas fotovoltaicos llave en mano para hogares: sin enganche y retorno ≤ 4 años.",
    accent: "#FF8A00",
    cta: "Cotizar mi casa",
    href: "/cotizar?tipo=residencial", // único que va a /cotizar
  },
  {
    icon: FiTool,
    title: "Soporte Técnico",
    desc: "Servicio preventivo-correctivo que garantiza 99 % de disponibilidad anual.",
    accent: "#1A73E8",
    cta: "Agendar mantenimiento",
    href: "/contacto#ready", // salta al ancla
  },
  {
    icon: FiDollarSign,
    title: "Financiamiento Verde",
    desc: "Plazos hasta 60 meses, tasa preferencial y deducible de impuestos.",
    accent: "#34A853",
    cta: "Solicitar crédito", // nuevo texto
    href: "/contacto#ready", // salta al ancla
  },
];

/* ---------- Tarjeta individual ---------- */
function ServiceCard({ icon: Icon, title, desc, accent, cta, href }) {
  const accentLight = alpha(accent, 0.12);
  const accentShadow = alpha(accent, 0.18);
  const leftGap = 32 + 72 + 16; // espacio ícono → texto

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
        bgcolor: "#fff",
        pt: 12,
        pb: 6,
        px: { xs: 4, md: 6 },
        pl: { xs: leftGap / 8, md: (leftGap + 8) / 8 },
        width: "100%",
        height: "100%",
        overflow: "hidden",
        boxShadow: `0 12px 36px ${alpha(
          "#000",
          0.07
        )}, 0 4px 12px ${accentShadow}`,
        transition: "transform .35s, box-shadow .35s",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: `0 20px 48px ${alpha(
            "#000",
            0.08
          )}, 0 10px 28px ${accentShadow}`,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: 4,
          borderTop: `6px solid ${accent}`,
        },
      }}
    >
      {/* Icono circular */}
      <Box
        sx={{
          position: "absolute",
          top: 40,
          left: 32,
          width: 72,
          height: 72,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: accentLight,
          color: accent,
          fontSize: 34,
          boxShadow: `0 4px 12px ${alpha(accent, 0.3)}`,
        }}
      >
        <Icon />
      </Box>

      <Typography variant="h6" fontWeight={700} mb={2}>
        {title}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        lineHeight={1.6}
        mb={5}
      >
        {desc}
      </Typography>

      <Button
        href={href}
        variant="contained"
        size="large"
        sx={{
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 20,
          px: 4,
          mt: "auto",
          bgcolor: accent,
          "&:hover": { bgcolor: accent },
        }}
      >
        {cta}
      </Button>
    </Box>
  );
}

/* ---------- Sección completa ---------- */
export default function Features() {
  const theme = useTheme();

  return (
    <Box
      id="servicios" /* ancla para scroll desde NavBar */
      component="section"
      sx={{
        py: { xs: 12, md: 16 },
        background: "linear-gradient(180deg,#fafafa 0%,#ffffff 55%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Typography
          variant="h4"
          align="center"
          fontWeight={700}
          mb={{ xs: 8, md: 10 }}
        >
          Nuestros Servicios
        </Typography>

        <Box
          sx={{
            display: "grid",
            gap: { xs: 6, md: 8 },
            gridTemplateColumns: {
              xs: "repeat(auto-fit,minmax(280px,1fr))",
              md: "repeat(3, 1fr)",
            },
            alignItems: "stretch",
          }}
        >
          {services.map((svc) => (
            <ServiceCard key={svc.title} {...svc} />
          ))}
        </Box>
      </Container>
    </Box>
  );
}
